import { GitBranch, Clock, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { DATA_FR } from '../data/fr';
import { LABELS } from '../data/labels';

export const PipelineStatus = ({ data, labels }: { data: typeof DATA_FR, labels: typeof LABELS['fr'] }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.1 }}
    className="card card-hover p-8 mb-8"
  >
    <div className="flex items-center justify-between mb-12">
      <div className="flex items-center gap-3">
        <div className="p-3 bg-tertiary-container rounded-xl">
          <GitBranch className="w-6 h-6 text-on-tertiary-container" />
        </div>
        <h2 className="text-xl font-bold text-on-surface">{labels.pipeline}</h2>
      </div>
      <div className="flex items-center gap-2 px-3 py-1 bg-primary-container text-on-primary-container rounded-full text-xs font-bold uppercase tracking-wider">
        <Clock className="w-3.5 h-3.5" />
        <span>{labels.running}</span>
      </div>
    </div>

    <div className="relative">
      {/* Background Track - Centered vertically with the nodes (h-12 is 48px, so center is 24px) */}
      <div className="absolute top-6 left-0 right-0 h-1 -translate-y-1/2 bg-surface-container-highest rounded-full z-0" />

      <div className="flex justify-between items-start relative z-10">
        {data.pipeline.map((stage, idx) => {
          const isPassed = stage.status === 'passed';
          const isRunning = stage.status === 'running';

          return (
            <div key={stage.stage} className="flex flex-col items-center flex-1 relative group">

              {/* Connector Line (Dynamic) - Connects to previous node */}
              {idx > 0 && (
                <div
                  className={`absolute top-6 right-[50%] w-full h-1 -translate-y-1/2 -z-10 origin-right transition-transform duration-700 ease-out
                    ${isPassed || isRunning ? 'bg-primary scale-x-100' : 'bg-transparent scale-x-0'}
                  `}
                />
              )}

              {/* Node */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: idx * 0.1 }}
                className={`
                  w-12 h-12 rounded-full flex items-center justify-center border-[4px] border-surface shadow-sm transition-all duration-300 z-10
                  ${isPassed
                    ? 'bg-primary text-on-primary scale-110'
                    : isRunning
                    ? 'bg-tertiary text-on-tertiary ring-4 ring-tertiary/20 scale-110'
                    : 'bg-surface-container-high text-on-surface-variant'}
                `}
              >
                {isPassed ? (
                  <CheckCircle2 className="w-6 h-6" />
                ) : isRunning ? (
                  <Clock className="w-6 h-6 animate-pulse" />
                ) : (
                  <div className="w-2.5 h-2.5 rounded-full bg-on-surface-variant/30" />
                )}
              </motion.div>

              {/* Label */}
              <div className="mt-4 text-center">
                <div className={`text-xs sm:text-sm font-bold transition-colors mb-0.5 ${
                  isPassed || isRunning ? 'text-on-surface' : 'text-on-surface-variant'
                }`}>
                  {stage.stage}
                </div>
                <div className="text-[10px] sm:text-xs text-on-surface-variant/60 font-mono">
                  {stage.duration}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </motion.div>
);
